import {
  BFF_REFRESH_PATH,
  CSRF_HEADER_NAME,
  STATE_CHANGING_METHODS,
  XSRF_COOKIE_NAME,
  buildLoginUrl,
  currentReturnUrl,
  readCookie,
} from '@/lib/bff'

/**
 * The one way this app talks to the platform.
 *
 * Every request the UI makes goes through here, and what it adds is the whole of what cookie
 * authentication needs:
 *
 *  - **`credentials: 'include'`**, so the session cookie travels. Same-origin makes this uncontroversial:
 *    `/api`, `/bff`, `/hubs` and `/signin-oidc` are served on this host by the orchestrator (checklist D5).
 *  - **The CSRF header on state-changing methods.** Cookie auth is ambient — the browser attaches it to a
 *    request some other page caused — where a bearer header never was. The `XSRF-TOKEN` cookie is readable
 *    by design and echoed as `X-CSRF`; the server compares it against the value held with the session, and
 *    a mismatch is a 403.
 *  - **One 401 handler.** A session can end mid-visit: it expires, the user signs out in another tab, or
 *    (step 05) their tenant is disabled. One refresh is attempted; if that fails, the browser goes to the
 *    sign-in once. Without a single place for this, a dashboard with six queries on it produces six error
 *    toasts and no way forward.
 *
 * There used to be nine copies of a `getAuthHeaders()` helper across `src/api/` doing the token half of
 * this. They are gone: a second copy of the CSRF rule is a second place for it to be forgotten, and the
 * symptom of forgetting it is a 403 on one mutation out of thirty.
 */

/** Set on a retried request so a refreshed session that still 401s does not loop. */
const RETRIED = Symbol('apiFetchRetried')

interface RetryableInit extends RequestInit {
  [RETRIED]?: boolean
}

/**
 * What happens when the session is gone for good. Replaceable so tests can assert the redirect without
 * leaving jsdom, and so a caller that wants to render a prompt instead of navigating can say so.
 */
let onUnauthenticated: (returnUrl: string) => void = (returnUrl) => {
  window.location.assign(buildLoginUrl(returnUrl))
}

/** Overrides the unauthenticated handler. Returns the previous one, so a test can put it back. */
export function setOnUnauthenticated(handler: (returnUrl: string) => void): (returnUrl: string) => void {
  const previous = onUnauthenticated
  onUnauthenticated = handler
  return previous
}

/**
 * Issues a request as the signed-in user.
 *
 * A 401 is answered with one `POST /bff/refresh` and one retry — the BFF rotates the tokens server-side
 * under a lock, so this is safe to have several requests do at once.
 *
 * **The refresh decides, not the retry.** If the refresh is refused the session is genuinely over, and the
 * browser is sent to sign in. If it succeeds, the session is alive by definition, so a 401 that survives
 * the retry is that endpoint's answer about *this* caller — a missing policy, a role they do not hold —
 * and the caller gets it back to render. Reading the second 401 as "signed out" is how one refusing route
 * becomes an endless trip through the identity provider.
 */
export async function apiFetch(input: string, init: RetryableInit = {}): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)

  if (STATE_CHANGING_METHODS.has(method)) {
    const csrf = readCookie(XSRF_COOKIE_NAME)
    if (csrf !== null) {
      headers.set(CSRF_HEADER_NAME, csrf)
    }
  }

  const response = await fetch(input, { ...init, method, headers, credentials: 'include' })

  if (response.status !== 401) {
    return response
  }

  if (init[RETRIED]) {
    // Refreshed, and this route still says no. The refresh below succeeded, so the session is provably
    // alive — this 401 belongs to the endpoint, not to the session, and sending the browser to sign in
    // would only bring it back to the same refusal. That loop is not hypothetical: on 2026-09-19 a single
    // route the gateway would not authenticate took the whole app round the identity provider once a
    // second. The caller gets the 401 and shows its own error.
    return response
  }

  const refreshed = await fetch(BFF_REFRESH_PATH, {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
  })

  if (refreshed.ok) {
    return apiFetch(input, { ...init, [RETRIED]: true })
  }

  onUnauthenticated(currentReturnUrl())
  return response
}

function csrfHeaders(): Record<string, string> {
  const csrf = readCookie(XSRF_COOKIE_NAME)
  return csrf === null ? {} : { [CSRF_HEADER_NAME]: csrf }
}
